import { toEndOfUtcDayString, toStartOfUtcDayString } from '@chainflip/utils/date';
import { formatUsdValue } from '@chainflip/utils/number';
import { ResponsiveLine, type Serie as Series, type SliceTooltipProps } from '@nivo/line';
import { differenceInDays, startOfHour } from 'date-fns';
import { ChartTooltip } from '@/shared/components/ChartTooltip';
import GraphLoader from '@/shared/components/GraphLoader';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import { getDateAxisTicks, getEmptyTickMap } from '@/shared/utils/charts';
import { getAccountLiquiditySnapshots } from 'packages/liquidity-provision/graphql/lp/portfolio';

const SliceTooltip = (props: SliceTooltipProps, isDaily: boolean) => {
  const point = props.slice.points[0];
  const date = new Date(point.data.x);

  return (
    <ChartTooltip activeXAxisItem={props.slice.x}>
      <div className="flex flex-col whitespace-nowrap text-center">
        <div className="text-12 text-cf-light-2">
          <div>{isDaily ? date.toLocaleString() : date.toLocaleDateString()}</div>
        </div>
        <div className="text-24">
          <span className="text-cf-white">{formatUsdValue(point.data.y as number)}</span>
        </div>
      </div>
    </ChartTooltip>
  );
};

const Graph = ({ data, isDaily }: { data: Series[]; isDaily: boolean }) => {
  const chartDatums = data[0].data;
  const maxYValue = Math.max(...chartDatums.map((item) => Number(item.y)));

  return (
    <ResponsiveLine
      data={data}
      colors={['#46DA93']}
      margin={{ top: 20, right: 20, bottom: 50, left: 62 }}
      xScale={{ type: 'time' }}
      yScale={{
        type: 'linear',
        min: 0,
        max: maxYValue * 1.1,
      }}
      curve="monotoneX"
      axisLeft={{
        format: (d) => (d < 0.01 ? d : formatUsdValue(d, false)),
        tickSize: 0,
        tickValues: 5,
      }}
      axisBottom={{
        format: (dateString) => {
          const date = new Date(dateString);

          if (isDaily) {
            return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: 'numeric' });
          }

          return new Date(dateString).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
          });
        },
        tickSize: 0,
        tickPadding: 10,
        tickRotation: -60,
        tickValues: getDateAxisTicks(chartDatums.map((item) => item.x)),
      }}
      enableGridX={false}
      gridYValues={5}
      enablePoints={false}
      enableArea
      areaOpacity={0.4}
      useMesh
      layers={[
        'grid',
        'axes',
        'areas',
        'crosshair',
        'lines',
        'markers',
        'points',
        'slices',
        'mesh',
      ]}
      defs={[
        {
          id: 'PortfolioGraph',
          type: 'linearGradient',
          colors: [
            { offset: 0, color: 'inherit', opacity: 1 },
            {
              offset: 100,
              color: 'inherit',
              opacity: 0.4,
            },
          ],
        },
      ]}
      fill={[{ match: '*', id: 'PortfolioGraph' }]}
      lineWidth={1}
      theme={{
        axis: {
          ticks: {
            text: {
              fill: '#878787',
              fontFamily: 'Aeonik-Medium',
              fontSize: 12,
            },
          },
        },
        grid: {
          line: {
            stroke: '#252525',
            strokeWidth: 1,
            strokeDasharray: 2,
          },
        },
        crosshair: {
          line: {
            stroke: '#FFFFFF',
            strokeOpacity: 0.4,
            strokeWidth: 1,
            strokeDasharray: '2',
          },
        },
      }}
      enableSlices="x"
      sliceTooltip={(props) => SliceTooltip(props, isDaily)}
      animate={false}
    />
  );
};

export const PortfolioLineChart = ({
  account,
  chartStartDate,
  chartEndDate,
  currentTotalLiquidity,
  totalLiquidityLoading,
}: {
  account: string;
  chartStartDate: Date;
  chartEndDate: Date;
  currentTotalLiquidity: number;
  totalLiquidityLoading: boolean;
}) => {
  const daysBack = differenceInDays(chartEndDate, chartStartDate);
  const isDaily = daysBack === 1;

  const groupBy = isDaily ? 'TIMESTAMP_TRUNCATED_TO_HOUR' : 'TIMESTAMP_TRUNCATED_TO_DAY';

  const { data: response, isLoading } = useGqlQuery(getAccountLiquiditySnapshots, {
    variables: {
      idSs58: account,
      minTimestamp: toStartOfUtcDayString(chartStartDate),
      maxTimestamp: toEndOfUtcDayString(chartEndDate),
      groupBy,
    },
    refetchInterval: 15_000,
    context: { clientName: 'lpProcessor' },
    select: (data) => {
      const leastDate = data.account?.snapshots.agg?.reduce(
        (acc, curr) => (!curr.keys?.[0] || acc < curr.keys?.[0] ? acc : curr.keys[0]),
        new Date().toISOString(),
      );
      const map = getEmptyTickMap(new Date(leastDate ?? new Date()), new Date(), (date) => ({
        x: date,
        y: 0,
      }));
      const currHour = startOfHour(Date.now()).getTime();
      const currDay = new Date(toStartOfUtcDayString(chartEndDate)).getTime();

      data.account?.snapshots?.agg?.forEach((node) => {
        const timestamp = node.keys?.[0] as string;
        if (!timestamp) return;

        const date = new Date(timestamp);
        const entry = map.get(date.valueOf());
        if (entry) {
          map.set(date.valueOf(), {
            ...entry,
            x: new Date(timestamp),
            y: Number(node.max?.totalLiquidityUsd ?? 0),
          });
        }
      });

      // If the current date point is the current hour or the current day, we want to show the current liquidity
      if (map.get(currHour)) {
        map.set(currHour, {
          x: new Date(currHour),
          y: currentTotalLiquidity,
        });
      } else if (map.get(currDay)) {
        map.set(currDay, {
          x: new Date(currDay),
          y: currentTotalLiquidity,
        });
      }

      return [...map.values()];
    },
  });
  const chartData = [{ id: 'Portfolio Value', data: response || [] }];

  return (
    <GraphLoader className="h-full w-full" loading={isLoading || totalLiquidityLoading}>
      {!isLoading && <Graph data={chartData} isDaily={isDaily} />}
    </GraphLoader>
  );
};
